import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import useMatchMedia from 'hooks/useMatchMedia';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  DropdownProps,
  DropdownRef,
} from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { Dropdown, Modal } from 'ncoded-component-library';

import './ResponsiveDropdown.styles.scss';

export type ResponsiveDropdownRef = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

type ResponsiveDropdownProps = {
  children?: ReactNode;
  className?: string;
  showModal?: boolean;
  trigger: JSX.Element;
  modalProps?: Partial<ModalProps>;
  dropdownProps?: Partial<Omit<DropdownProps, 'ref'>>;
  onOpen?: () => void;
  onClose?: () => void;
};

const ResponsiveDropdown: React.ForwardRefRenderFunction<
  ResponsiveDropdownRef,
  ResponsiveDropdownProps
> = (props, ref) => {
  const isPhablet = useMatchMedia('isPhablet');

  const {
    children,
    className,
    trigger,
    modalProps,
    dropdownProps,
    showModal = isPhablet,
    onOpen,
    onClose,
  } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  const classes = classNames('responsive-dropdown', className);

  const isOpen = useMemo(() => {
    if (showModal) return !!modal?.openState;
    return !!dropdown?.isOpen;
  }, [dropdown?.isOpen, modal?.openState, showModal]);

  const open = useCallback(() => {
    if (showModal) modal?.open();
    else dropdown?.setIsOpen(true);

    onOpen?.();
  }, [dropdown, modal, onOpen, showModal]);

  const close = useCallback(() => {
    if (showModal) modal?.close();
    else dropdown?.setIsOpen(false);

    onClose?.();
  }, [dropdown, modal, onClose, showModal]);

  useImperativeHandle(ref, () => ({ isOpen, open, close }), [
    close,
    isOpen,
    open,
  ]);

  const Trigger = useMemo(
    () => (
      <div
        className={classes}
        onClick={(e) => {
          e.stopPropagation();
          open();
        }}
      >
        {trigger}
      </div>
    ),
    [classes, open, trigger],
  );

  if (showModal) {
    return (
      <>
        {Trigger}
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'none' }}>
          <Modal ref={modalRef} {...modalProps}>
            {children}
          </Modal>
        </div>
      </>
    );
  }

  return (
    <Dropdown
      ref={dropdownRef}
      trigger={Trigger}
      renderAsPortal
      {...dropdownProps}
      portalClassName={classNames(
        'responsive-dropdown__portal',
        dropdownProps?.portalClassName,
      )}
    >
      {children}
    </Dropdown>
  );
};

export default forwardRef(ResponsiveDropdown);
